import type { Event, EventTypeOf, ReadEvent } from '../core/types';

// #region Core Interfaces

/**
 * Base entity for read models
 */
export interface ReadModelEntity {
  // The interface allows for any structure
  [key: string]: unknown;
}

/**
 * Common options for read operations
 */
export interface ReadOptions {
  tableName?: string; // Optional table name for multi-table support
  consistency?: 'strong' | 'eventual';
  cacheTtl?: number;
  type?: 'text' | 'json' | 'arrayBuffer' | 'stream'; // Format to return the value in
  [key: string]: unknown; // Extension point for database-specific options
}

/**
 * Common options for write operations
 */
export interface WriteOptions {
  tableName?: string; // Optional table name for multi-table support
  condition?: ConditionalExpression;
  returnValue?: 'none' | 'all' | 'oldValue' | 'newValue';
  [key: string]: unknown; // Extension point for database-specific options
}

/**
 * Options for item expiration
 */
export interface ExpirationOptions {
  expiration?: number; // Absolute timestamp in seconds
  expirationTtl?: number; // Relative TTL in seconds
}

/**
 * Storage metadata options
 */
export interface MetadataOptions {
  metadata?: Record<string, unknown>;
}

/**
 * Query options with common parameters
 */
export interface QueryOptions {
  tableName?: string; // Optional table name for multi-table support
  limit?: number;
  offset?: number;
  cursor?: string; // Continuation token from a previous operation
  prefix?: string; // Key prefix for key-value stores
  sort?: Array<{ field: string; direction: 'asc' | 'desc' }>;
  consistency?: 'strong' | 'eventual';
  cacheTtl?: number;
  type?: 'text' | 'json' | 'arrayBuffer' | 'stream'; // Format to return values in
  [key: string]: unknown; // Extension point for database-specific options
}

/**
 * Conditional expression for operations
 */
export interface ConditionalExpression {
  // Common conditions that can be translated to different databases
  type:
    | 'equals'
    | 'notEquals'
    | 'exists'
    | 'notExists'
    | 'greaterThan'
    | 'lessThan'
    | 'between'
    | 'beginsWith'
    | 'custom';
  field?: string;
  value?: unknown;
  values?: unknown[]; // For between, in, etc.
  customExpression?: string; // For database-specific expressions
  [key: string]: unknown; // Extension for database-specific attributes
}

/**
 * Query filter operators
 */
export interface QueryFilter {
  [field: string]:
    | {
        // Common operators supported across databases
        $eq?: unknown;
        $ne?: unknown;
        $gt?: unknown;
        $gte?: unknown;
        $lt?: unknown;
        $lte?: unknown;
        $in?: unknown[];
        $nin?: unknown[];
        $exists?: boolean;
        $regex?: string;
        $beginsWith?: string;

        // Logical operators
        $and?: QueryFilter[];
        $or?: QueryFilter[];
        $not?: QueryFilter;

        // Database-specific extensions
        [operator: string]: unknown;
      }
    | unknown; // Allow direct value for equality comparison
}

/**
 * Index definition for store configuration
 */
export interface IndexDefinition {
  name: string;
  fields: string[];
  type?: string; // 'primary' | 'secondary' | 'global' | 'local' | 'text' | 'geo' | etc.
  unique?: boolean;
  sparse?: boolean;
  [key: string]: unknown; // Database-specific options
}

/**
 * Store capabilities for runtime feature detection
 */
export interface StoreCapabilities {
  storeType: string; // 'kv' | 'dynamodb' | 'mongodb' | 'd1' | etc.
  features: {
    transactions: boolean;
    advancedQueries: boolean;
    aggregation: boolean;
    conditionalWrites: boolean;
    ttl: boolean;
    streaming: boolean;
  };
  limits: {
    maxBatchSize?: number;
    maxItemSize?: number;
    maxTransactionSize?: number;
  };
  queryOperators: string[]; // Supported operators
  indexTypes: string[]; // Supported index types
  [key: string]: unknown; // Database-specific capabilities
}

export type DatabaseItem<T> = T & {
  [key: string]: unknown;
};

/**
 * Core read model store interface compatible with multiple database types.
 * Provides methods for storing, retrieving, deleting, and listing key-value pairs.
 */
export interface IReadModelStore {
  /**
   * Stores a value with the given key. If the key already exists, the value will be overwritten.
   *
   * @param key - The key under which to store the value
   * @param value - The value to store (string, ArrayBuffer, or any type that can be serialized to JSON)
   * @param options - Optional settings for the storage operation
   * @returns A promise that resolves when the value has been stored
   */
  put<T = unknown>(
    key: string,
    value: T,
    options?: WriteOptions & ExpirationOptions & MetadataOptions
  ): Promise<void>;

  /**
   * Retrieves a value by its key.
   *
   * @param key - The key of the value to retrieve
   * @param options - Optional settings for the retrieval operation
   * @returns A promise that resolves with the value, or null if not found
   */
  get<T = unknown>(key: string, options?: ReadOptions): Promise<T | null>;

  /**
   * Retrieves all values, optionally filtered by a key prefix.
   *
   * @param options - Optional settings for retrieving all values
   * @returns A promise that resolves with an array of key-value pairs
   */
  getAll<T = unknown>(
    options?: QueryOptions
  ): Promise<Array<DatabaseItem<T> | unknown>>;

  /**
   * Deletes a value by its key.
   *
   * @param key - The key of the value to delete
   * @param options - Optional settings for the delete operation
   * @returns A promise that resolves to true if the key existed and was deleted, false otherwise
   */
  delete(key: string, options?: WriteOptions): Promise<boolean>;

  /**
   * Lists keys in the store, optionally filtered by prefix.
   *
   * @param options - Optional settings for the list operation
   * @returns A promise that resolves with the list result containing keys and metadata
   */
  listKeys?(options?: {
    tableName?: string;
    prefix?: string; // Filter keys by this prefix
    limit?: number; // Maximum number of keys to return
    cursor?: string; // Continuation token from a previous list operation
  }): Promise<{
    keys: Array<{
      name: string; // Key name
      expiration?: number; // Unix timestamp of when the key expires (if set)
      metadata?: Record<string, unknown>; // Key metadata (if set)
    }>;
    list_complete: boolean; // Whether there are more keys to fetch
    cursor?: string; // Continuation token for subsequent list operations
  }>;

  /**
   * Batch retrieval of multiple items by their keys
   *
   * @param keys - Array of keys to retrieve
   * @param options - Optional settings for the batch get operation
   * @returns A promise that resolves with an array of key-value pairs
   */
  batchGet?<T = unknown>(
    keys: string[],
    options?: ReadOptions
  ): Promise<Array<DatabaseItem<T> | unknown>>;

  /**
   * Batch storage of multiple items
   *
   * @param items - Array of key-value pairs to store
   * @param options - Optional settings for the batch put operation
   * @returns A promise that resolves when all values have been stored
   */
  batchPut?<T = unknown>(
    items: Array<{ key: string; value: T }>,
    options?: WriteOptions & ExpirationOptions
  ): Promise<void>;

  /**
   * Batch deletion of multiple items
   *
   * @param keys - Array of keys to delete
   * @param options - Optional settings for the batch delete operation
   * @returns A promise that resolves with an array of booleans indicating success/failure
   */
  batchDelete?(keys: string[], options?: WriteOptions): Promise<boolean[]>;

  /**
   * Query items using filter criteria or options
   *
   * @param options - Query options and filter criteria
   * @returns A promise that resolves with matching items
   */
  query?<T = unknown>(
    options: QueryOptions & {
      filter?: ((value: unknown) => boolean) | QueryFilter;
    }
  ): Promise<Array<DatabaseItem<T> | unknown>>;

  /**
   * Returns the capabilities of this store implementation
   *
   * @returns Store capabilities object
   */
  getCapabilities?(): StoreCapabilities;

  /**
   * Gets access to the native database client
   * Allows for direct operations using the underlying database API
   *
   * @returns The native client instance
   */
  getNativeClient?<C = unknown>(): C;
}

/**
 * Interface for a projection that can handle events
 * @template TEvent - The event type(s) this projection can handle
 */
export interface Projection<TEvent extends Event> {
  /**
   * List of event types this projection can handle
   */
  readonly supportedEventTypes: EventTypeOf<TEvent>[];

  /**
   * Process events through this projection
   *
   * @param events - Events to process
   */
  processEvents(events: ReadEvent<TEvent>[]): Promise<void>;
}

// #endregion

// #region Store Implementations

/**
 * In-memory implementation of the IReadModelStore interface
 * Useful for testing and development
 */
export class InMemoryReadModelStore implements IReadModelStore {
  private store = new Map<string, unknown>();
  private metadata = new Map<
    string,
    { expiration?: number; metadata?: Record<string, unknown> }
  >();

  async put<T = unknown>(
    key: string,
    value: T,
    options?: WriteOptions & ExpirationOptions & MetadataOptions
  ): Promise<void> {
    const prefixedKey = this.getPrefixedKey(key, options?.tableName);

    this.store.set(prefixedKey, value);

    if (options?.expiration || options?.expirationTtl || options?.metadata) {
      const meta: { expiration?: number; metadata?: Record<string, unknown> } =
        {};

      if (options.expiration) {
        meta.expiration = options.expiration;
      } else if (options.expirationTtl) {
        meta.expiration = Math.floor(Date.now() / 1000) + options.expirationTtl;
      }

      if (options.metadata) {
        meta.metadata = options.metadata;
      }

      this.metadata.set(prefixedKey, meta);
    }
  }

  async get<T = unknown>(
    key: string,
    options?: ReadOptions
  ): Promise<T | null> {
    const prefixedKey = this.getPrefixedKey(key, options?.tableName);

    // Check if key has expired
    const meta = this.metadata.get(prefixedKey);
    if (meta?.expiration && meta.expiration <= Math.floor(Date.now() / 1000)) {
      this.store.delete(prefixedKey);
      this.metadata.delete(prefixedKey);
      return null;
    }

    const value = this.store.get(prefixedKey);
    return value === undefined ? null : (value as T);
  }

  async getAll<T = unknown>(
    options?: QueryOptions
  ): Promise<Array<{ key: string; value: T }>> {
    const result: Array<{ key: string; value: T }> = [];
    const prefix = this.buildPrefix(options?.tableName, options?.prefix);

    for (const [key, value] of this.store.entries()) {
      if (!prefix || key.startsWith(prefix)) {
        // Check expiration
        const meta = this.metadata.get(key);
        if (
          meta?.expiration &&
          meta.expiration <= Math.floor(Date.now() / 1000)
        ) {
          this.store.delete(key);
          this.metadata.delete(key);
          continue;
        }

        const originalKey = this.removePrefix(key, options?.tableName);
        result.push({ key: originalKey, value: value as T });
      }
    }

    // Apply limit if specified
    if (options?.limit) {
      return result.slice(0, options.limit);
    }

    return result;
  }

  async delete(key: string, options?: WriteOptions): Promise<boolean> {
    const prefixedKey = this.getPrefixedKey(key, options?.tableName);
    const existed = this.store.has(prefixedKey);

    this.store.delete(prefixedKey);
    this.metadata.delete(prefixedKey);

    return existed;
  }

  async listKeys(options?: {
    tableName?: string;
    prefix?: string;
    limit?: number;
    cursor?: string;
  }): Promise<{
    keys: Array<{
      name: string;
      expiration?: number;
      metadata?: Record<string, unknown>;
    }>;
    list_complete: boolean;
    cursor?: string;
  }> {
    const keys: Array<{
      name: string;
      expiration?: number;
      metadata?: Record<string, unknown>;
    }> = [];

    const prefix = this.buildPrefix(options?.tableName, options?.prefix);

    for (const [key] of this.store.entries()) {
      if (!prefix || key.startsWith(prefix)) {
        const meta = this.metadata.get(key);
        keys.push({
          name: key,
          expiration: meta?.expiration,
          metadata: meta?.metadata,
        });
      }
    }

    // Apply limit if specified
    const limit = options?.limit;
    if (limit) {
      return {
        keys: keys.slice(0, limit),
        list_complete: keys.length <= limit,
      };
    }

    return {
      keys,
      list_complete: true,
    };
  }

  async batchGet<T = unknown>(
    keys: string[],
    options?: ReadOptions
  ): Promise<Array<{ key: string; value: T | null }>> {
    return Promise.all(
      keys.map(async (key) => ({
        key,
        value: await this.get<T>(key, options),
      }))
    );
  }

  async batchPut<T = unknown>(
    items: Array<{ key: string; value: T }>,
    options?: WriteOptions & ExpirationOptions
  ): Promise<void> {
    await Promise.all(
      items.map(async (item) => {
        await this.put(item.key, item.value, options);
      })
    );
  }

  async batchDelete(
    keys: string[],
    options?: WriteOptions
  ): Promise<boolean[]> {
    return Promise.all(
      keys.map(async (key) => await this.delete(key, options))
    );
  }

  async query<T = unknown>(
    options: QueryOptions & {
      filter?: ((value: unknown) => boolean) | QueryFilter;
    }
  ): Promise<Array<{ key: string; value: T }>> {
    const allItems = await this.getAll<T>(options);

    if (!options.filter) {
      return allItems;
    }

    if (typeof options.filter === 'function') {
      return allItems.filter((item) =>
        // biome-ignore lint/complexity/noBannedTypes: Function type needed for filter callback compatibility
        (options.filter as Function)(item.value)
      );
    }

    // Basic filter implementation for common cases
    return allItems.filter((item) => {
      const filter = options.filter as QueryFilter;
      for (const [field, condition] of Object.entries(filter)) {
        // biome-ignore lint/suspicious/noExplicitAny: Dynamic field access requires any type
        const value = (item.value as any)?.[field];

        if (typeof condition === 'object' && condition !== null) {
          if ('$eq' in condition && value !== condition.$eq) return false;
          if ('$ne' in condition && value === condition.$ne) return false;
          // biome-ignore lint/suspicious/noExplicitAny: Type comparison operators need any for generic value comparison
          if ('$gt' in condition && value <= (condition.$gt as any))
            return false;
          // biome-ignore lint/suspicious/noExplicitAny: Type comparison operators need any for generic value comparison
          if ('$gte' in condition && value < (condition.$gte as any))
            return false;
          // biome-ignore lint/suspicious/noExplicitAny: Type comparison operators need any for generic value comparison
          if ('$lt' in condition && value >= (condition.$lt as any))
            return false;
          // biome-ignore lint/suspicious/noExplicitAny: Type comparison operators need any for generic value comparison
          if ('$lte' in condition && value > (condition.$lte as any))
            return false;
          // biome-ignore lint/suspicious/noExplicitAny: Array operations need any for generic value types
          if ('$in' in condition && !(condition.$in as any[])?.includes(value))
            return false;
          // biome-ignore lint/suspicious/noExplicitAny: Array operations need any for generic value types
          if ('$nin' in condition && (condition.$nin as any[])?.includes(value))
            return false;
          if ('$exists' in condition) {
            const exists = value !== undefined && value !== null;
            if (condition.$exists !== exists) return false;
          }
          if ('$beginsWith' in condition && typeof value === 'string') {
            if (!value.startsWith(condition.$beginsWith as string))
              return false;
          }
        } else {
          if (value !== condition) return false;
        }
      }
      return true;
    });
  }

  getCapabilities(): StoreCapabilities {
    return {
      storeType: 'in-memory',
      features: {
        transactions: false,
        advancedQueries: true,
        aggregation: false,
        conditionalWrites: false,
        ttl: true,
        streaming: false,
      },
      limits: {
        maxBatchSize: 1000,
        maxItemSize: 64 * 1024 * 1024, // 64MB
      },
      queryOperators: [
        '$eq',
        '$ne',
        '$gt',
        '$gte',
        '$lt',
        '$lte',
        '$in',
        '$nin',
        '$exists',
        '$beginsWith',
      ],
      indexTypes: [],
    };
  }

  getNativeClient<C = Map<string, unknown>>(): C {
    return this.store as unknown as C;
  }

  /**
   * Clear all data (useful for testing)
   */
  clear(): void {
    this.store.clear();
    this.metadata.clear();
  }

  private getPrefixedKey(key: string, tableName?: string): string {
    return tableName ? `${tableName}:${key}` : key;
  }

  private removePrefix(key: string, tableName?: string): string {
    if (tableName && key.startsWith(`${tableName}:`)) {
      return key.substring(tableName.length + 1);
    }
    return key;
  }

  private buildPrefix(tableName?: string, prefix?: string): string | undefined {
    if (tableName && prefix) {
      return `${tableName}:${prefix}`;
    }
    if (tableName) {
      return `${tableName}:`;
    }
    return prefix;
  }
}

/**
 * Cloudflare KV implementation of the IReadModelStore interface.
 * Uses Cloudflare's KV namespace to store, retrieve, delete, and list key-value pairs.
 * Updated to use native batch operations for better performance.
 */
export class KVReadModelStore implements IReadModelStore {
  /**
   * Creates a new KV read model store.
   *
   * @param namespace - The Cloudflare KV namespace to use for storage
   * @param options - Optional configuration for the store
   */
  constructor(
    // biome-ignore lint/suspicious/noExplicitAny: placing any here to remove dependency on KVNamespace type
    private readonly namespace: any,
    private readonly options: {
      /**
       * Optional prefix to apply to all keys
       * This can be used to create virtual "tables" within a single KV namespace
       */
      defaultPrefix?: string;
    } = {}
  ) {}

  /**
   * Stores a value with the given key.
   *
   * @param key - The key under which to store the value
   * @param value - The value to store (will be serialized to JSON if it's not a string or ArrayBuffer)
   * @param options - Optional settings for the storage operation
   * @returns A promise that resolves when the value has been stored
   */
  async put<T = unknown>(
    key: string,
    value: T,
    options?: WriteOptions & ExpirationOptions & MetadataOptions
  ): Promise<void> {
    const prefixedKey = this.getPrefixedKey(key, options?.tableName);

    // Handle different value types for KV storage
    let serializedValue: string | ArrayBuffer | ReadableStream;
    if (typeof value === 'string') {
      serializedValue = value;
    } else if (value instanceof ArrayBuffer) {
      serializedValue = value;
    } else if (value instanceof ReadableStream) {
      serializedValue = value;
    } else {
      // JSON serialize objects and other types
      serializedValue = JSON.stringify(value);
    }

    // Build KV put options
    // biome-ignore lint/suspicious/noExplicitAny: KV options object needs any type for Cloudflare KV compatibility
    const kvOptions: any = {};
    if (options?.expiration) {
      kvOptions.expiration = options.expiration;
    }
    if (options?.expirationTtl) {
      kvOptions.expirationTtl = options.expirationTtl;
    }
    if (options?.metadata) {
      kvOptions.metadata = options.metadata;
    }

    try {
      await this.namespace.put(prefixedKey, serializedValue, kvOptions);
    } catch (error) {
      throw new Error(
        `Failed to store key "${key}": ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Retrieves a value by its key.
   *
   * @param key - The key of the value to retrieve
   * @param options - Optional settings for the retrieval operation
   * @returns A promise that resolves with the value, or null if not found
   */
  async get<T = unknown>(
    key: string,
    options?: ReadOptions
  ): Promise<T | null> {
    const prefixedKey = this.getPrefixedKey(key, options?.tableName);
    const type = options?.type || 'json';

    try {
      // biome-ignore lint/suspicious/noExplicitAny: KV get result type varies based on type parameter
      let result: any;

      if (type === 'json') {
        result = await this.namespace.get(prefixedKey, {
          type: 'json',
          cacheTtl: options?.cacheTtl,
        });
      } else if (type === 'text') {
        result = await this.namespace.get(prefixedKey, {
          type: 'text',
          cacheTtl: options?.cacheTtl,
        });
      } else if (type === 'arrayBuffer') {
        result = await this.namespace.get(prefixedKey, {
          type: 'arrayBuffer',
          cacheTtl: options?.cacheTtl,
        });
      } else if (type === 'stream') {
        result = await this.namespace.get(prefixedKey, {
          type: 'stream',
          cacheTtl: options?.cacheTtl,
        });
      } else {
        result = await this.namespace.get(prefixedKey, {
          cacheTtl: options?.cacheTtl,
        });
      }

      return result as T | null;
    } catch (error) {
      throw new Error(
        `Failed to retrieve key "${key}": ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Retrieves all values, optionally filtered by a key prefix.
   *
   * @param options - Optional settings for retrieving all values
   * @returns A promise that resolves with an array of key-value pairs
   */
  async getAll<T = unknown>(
    options?: QueryOptions
  ): Promise<Array<{ key: string; value: T }>> {
    const result: Array<{ key: string; value: T }> = [];
    let cursor: string | undefined;
    const limit = options?.limit || 1000;
    let remainingLimit = limit;

    // Build prefix for listing
    const prefix = this.buildListPrefix(options?.tableName, options?.prefix);

    try {
      do {
        const listResult = await this.namespace.list({
          prefix,
          limit: Math.min(remainingLimit, 1000),
          cursor,
        });

        if (listResult.keys.length === 0) {
          break;
        }

        // Extract just the key names for batch get
        // biome-ignore lint/suspicious/noExplicitAny: KV list result key structure uses any for name field
        const keyNames = listResult.keys.map((k: { name: any }) => k.name);
        const batchResult = await this.batchGetInternal(keyNames, options);

        // Process results and remove prefix from keys
        for (const item of batchResult) {
          if (item.value !== null) {
            const originalKey = this.removePrefix(item.key, options?.tableName);
            result.push({ key: originalKey, value: item.value as T });
          }
        }

        cursor = listResult.cursor;
        remainingLimit -= keyNames.length;
      } while (cursor && remainingLimit > 0);

      return result;
    } catch (error) {
      throw new Error(
        `Failed to retrieve all keys: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Deletes a value by its key.
   *
   * @param key - The key of the value to delete
   * @param options - Optional settings for the delete operation
   * @returns A promise that resolves to true if the key existed and was deleted, false otherwise
   */
  async delete(key: string, options?: WriteOptions): Promise<boolean> {
    const prefixedKey = this.getPrefixedKey(key, options?.tableName);

    try {
      // Check if the key exists before deletion
      const exists = (await this.namespace.get(prefixedKey)) !== null;
      await this.namespace.delete(prefixedKey);
      return exists;
    } catch (error) {
      throw new Error(
        `Failed to delete key "${key}": ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Lists keys in the store, optionally filtered by prefix.
   *
   * @param options - Optional settings for the list operation
   * @returns A promise that resolves with the list result containing keys and metadata
   */
  async listKeys(options?: {
    tableName?: string;
    prefix?: string;
    limit?: number;
    cursor?: string;
  }): Promise<{
    keys: Array<{
      name: string;
      expiration?: number;
      metadata?: Record<string, unknown>;
    }>;
    list_complete: boolean;
    cursor?: string;
  }> {
    const prefix = this.buildListPrefix(options?.tableName, options?.prefix);

    try {
      const result = await this.namespace.list({
        prefix,
        limit: options?.limit || 1000,
        cursor: options?.cursor,
      });

      return {
        keys: result.keys.map(
          (key: {
            // biome-ignore lint/suspicious/noExplicitAny: KV list result structure uses any for key name field
            name: any;
            // biome-ignore lint/suspicious/noExplicitAny: KV list result structure uses any for expiration field
            expiration: any;
            metadata: Record<string, unknown>;
          }) => ({
            name: key.name,
            expiration: key.expiration,
            metadata: key.metadata as Record<string, unknown>,
          })
        ),
        list_complete: result.list_complete,
        cursor: result.cursor,
      };
    } catch (error) {
      throw new Error(
        `Failed to list keys: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Native batch retrieval of multiple items by their keys using KV's new batch API
   * This is much more efficient than individual get operations
   *
   * @param keys - Array of keys to retrieve (max 100 keys)
   * @param options - Optional settings for the batch get operation
   * @returns A promise that resolves with an array of key-value pairs
   */
  async batchGet<T = unknown>(
    keys: string[],
    options?: ReadOptions
  ): Promise<Array<{ key: string; value: T | null }>> {
    if (keys.length === 0) {
      return [];
    }

    // Split into chunks of 100 (KV limit)
    const chunks: string[][] = [];
    for (let i = 0; i < keys.length; i += 100) {
      chunks.push(keys.slice(i, i + 100));
    }

    const results: Array<{ key: string; value: T | null }> = [];

    try {
      for (const chunk of chunks) {
        const chunkResults = await this.batchGetInternal<T>(chunk, options);
        results.push(...chunkResults);
      }

      return results;
    } catch (error) {
      throw new Error(
        `Failed to batch get keys: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Internal method for batch get operations
   */
  private async batchGetInternal<T = unknown>(
    keys: string[],
    options?: ReadOptions
  ): Promise<Array<{ key: string; value: T | null }>> {
    const prefixedKeys = keys.map((key) =>
      this.getPrefixedKey(key, options?.tableName)
    );
    const type = options?.type || 'json';

    // biome-ignore lint/suspicious/noExplicitAny: KV batch result values can be any type based on storage format
    let batchResult: Map<string, any>;

    if (type === 'json') {
      batchResult = await this.namespace.get(prefixedKeys, {
        type: 'json',
        cacheTtl: options?.cacheTtl,
      });
    } else if (type === 'text') {
      batchResult = await this.namespace.get(prefixedKeys, {
        type: 'text',
        cacheTtl: options?.cacheTtl,
      });
    } else {
      // For arrayBuffer and stream, fall back to individual calls as batch doesn't support them
      return Promise.all(
        keys.map(async (key) => {
          const value = await this.get<T>(key, options);
          return { key, value };
        })
      );
    }

    // Convert Map results back to our format
    return keys.map((originalKey) => {
      const prefixedKey = this.getPrefixedKey(originalKey, options?.tableName);
      const value = batchResult.get(prefixedKey) || null;
      return { key: originalKey, value: value as T | null };
    });
  }

  /**
   * Batch storage of multiple items
   */
  async batchPut<T = unknown>(
    items: Array<{ key: string; value: T }>,
    options?: WriteOptions & ExpirationOptions
  ): Promise<void> {
    // KV doesn't have native batch put in runtime API, so we use Promise.all
    // but limit concurrency to avoid overwhelming the API
    const concurrency = 25; // Conservative limit
    const chunks: Array<Array<{ key: string; value: T }>> = [];

    for (let i = 0; i < items.length; i += concurrency) {
      chunks.push(items.slice(i, i + concurrency));
    }

    try {
      for (const chunk of chunks) {
        await Promise.all(
          chunk.map((item) => this.put(item.key, item.value, options))
        );
      }
    } catch (error) {
      throw new Error(
        `Failed to batch put items: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Batch deletion of multiple items
   */
  async batchDelete(
    keys: string[],
    options?: WriteOptions
  ): Promise<boolean[]> {
    if (keys.length === 0) {
      return [];
    }

    const concurrency = 25; // Conservative limit
    const chunks: string[][] = [];

    for (let i = 0; i < keys.length; i += concurrency) {
      chunks.push(keys.slice(i, i + concurrency));
    }

    const results: boolean[] = [];

    try {
      for (const chunk of chunks) {
        const chunkResults = await Promise.all(
          chunk.map((key) => this.delete(key, options))
        );
        results.push(...chunkResults);
      }

      return results;
    } catch (error) {
      throw new Error(
        `Failed to batch delete keys: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Enhanced query with better prefix support
   */
  async query<T = unknown>(
    options: QueryOptions & {
      filter?: ((value: unknown) => boolean) | QueryFilter;
    }
  ): Promise<Array<{ key: string; value: T }>> {
    // For KV, we primarily support prefix-based queries
    if (options.prefix) {
      return this.getAll<T>(options);
    }

    // For complex filters, we need to get all items first (not efficient for large datasets)
    const allItems = await this.getAll<T>(options);

    if (!options.filter) {
      return allItems;
    }

    if (typeof options.filter === 'function') {
      return allItems.filter((item) =>
        // biome-ignore lint/complexity/noBannedTypes: Function type needed for filter callback compatibility
        (options.filter as Function)(item.value)
      );
    }

    // Simple filter implementation - for production use, consider more sophisticated querying
    const filter = options.filter as QueryFilter;
    return allItems.filter((item) => {
      for (const [field, condition] of Object.entries(filter)) {
        // biome-ignore lint/suspicious/noExplicitAny: Dynamic field access requires any type
        const value = (item.value as any)?.[field];

        if (typeof condition === 'object' && condition !== null) {
          // Handle query operators
          if ('$eq' in condition && value !== condition.$eq) return false;
          if ('$ne' in condition && value === condition.$ne) return false;
          if ('$beginsWith' in condition && typeof value === 'string') {
            if (!value.startsWith(condition.$beginsWith as string))
              return false;
          }
          // Add more operators as needed
        } else {
          if (value !== condition) return false;
        }
      }
      return true;
    });
  }

  /**
   * Returns enhanced capabilities for KV store
   */
  getCapabilities(): StoreCapabilities {
    return {
      storeType: 'cloudflare-kv',
      features: {
        transactions: false,
        advancedQueries: false, // Limited to prefix-based queries
        aggregation: false,
        conditionalWrites: false,
        ttl: true,
        streaming: true,
      },
      limits: {
        maxItemSize: 25 * 1024 * 1024, // 25MB
        maxBatchSize: 100, // Native batch get limit
        maxTransactionSize: 0,
      },
      queryOperators: ['$eq', '$ne', '$beginsWith'],
      indexTypes: ['prefix'],
    };
  }

  /**
   * Gets access to the native KV namespace
   */

  // biome-ignore lint/suspicious/noExplicitAny: placing any here to remove dependency on KVNamespace type
  getNativeClient<C = any>(): C {
    return this.namespace as unknown as C;
  }

  /**
   * Helper method to add metadata support
   */
  async getWithMetadata<T = unknown>(
    key: string,
    options?: ReadOptions
  ): Promise<{
    value: T | null;
    metadata: Record<string, unknown> | null;
  }> {
    const prefixedKey = this.getPrefixedKey(key, options?.tableName);
    const type = options?.type || 'json';

    try {
      const result = await this.namespace.getWithMetadata(prefixedKey, {
        type: type as 'json' | 'text' | 'arrayBuffer' | 'stream',
        cacheTtl: options?.cacheTtl,
      });

      return {
        value: result.value as T | null,
        metadata: result.metadata as Record<string, unknown> | null,
      };
    } catch (error) {
      throw new Error(
        `Failed to retrieve key with metadata "${key}": ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * Enhanced batch get with metadata support
   */
  async batchGetWithMetadata<T = unknown>(
    keys: string[],
    options?: ReadOptions
  ): Promise<
    Array<{
      key: string;
      value: T | null;
      metadata: Record<string, unknown> | null;
    }>
  > {
    if (keys.length === 0) {
      return [];
    }

    // Split into chunks of 100
    const chunks: string[][] = [];
    for (let i = 0; i < keys.length; i += 100) {
      chunks.push(keys.slice(i, i + 100));
    }

    const results: Array<{
      key: string;
      value: T | null;
      metadata: Record<string, unknown> | null;
    }> = [];

    try {
      for (const chunk of chunks) {
        const prefixedKeys = chunk.map((key) =>
          this.getPrefixedKey(key, options?.tableName)
        );
        const type = options?.type || 'json';

        // biome-ignore lint/suspicious/noExplicitAny: KV batch result with metadata can contain any value types
        let batchResult: Map<string, { value: any; metadata: any }>;

        if (type === 'json' || type === 'text') {
          batchResult = await this.namespace.getWithMetadata(prefixedKeys, {
            type: type as 'json' | 'text',
            cacheTtl: options?.cacheTtl,
          });
        } else {
          // For arrayBuffer and stream, fall back to individual calls
          const chunkResults = await Promise.all(
            chunk.map(async (key) => {
              const result = await this.getWithMetadata<T>(key, options);
              return { key, value: result.value, metadata: result.metadata };
            })
          );
          results.push(...chunkResults);
          continue;
        }

        // Convert Map results back to our format
        for (const originalKey of chunk) {
          const prefixedKey = this.getPrefixedKey(
            originalKey,
            options?.tableName
          );
          const result = batchResult.get(prefixedKey);

          results.push({
            key: originalKey,
            value: (result?.value as T | null) || null,
            metadata:
              (result?.metadata as Record<string, unknown> | null) || null,
          });
        }
      }

      return results;
    } catch (error) {
      throw new Error(
        `Failed to batch get with metadata: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  // Helper methods

  private getPrefixedKey(key: string, tableName?: string): string {
    if (tableName) {
      return `${tableName}:${key}`;
    }
    if (this.options.defaultPrefix) {
      return `${this.options.defaultPrefix}${key}`;
    }
    return key;
  }

  private removePrefix(key: string, tableName?: string): string {
    if (tableName && key.startsWith(`${tableName}:`)) {
      return key.substring(tableName.length + 1);
    }
    if (
      this.options.defaultPrefix &&
      key.startsWith(this.options.defaultPrefix)
    ) {
      return key.substring(this.options.defaultPrefix.length);
    }
    return key;
  }

  private buildListPrefix(
    tableName?: string,
    prefix?: string
  ): string | undefined {
    if (tableName && prefix) {
      return `${tableName}:${prefix}`;
    }
    if (tableName) {
      return `${tableName}:`;
    }
    if (this.options.defaultPrefix) {
      if (prefix) {
        return `${this.options.defaultPrefix}${prefix}`;
      }
      return this.options.defaultPrefix;
    }
    return prefix;
  }
}

/**
 * HTTP-based external store (for APIs, databases, etc.)
 */
export class HttpReadModelStore implements IReadModelStore {
  constructor(
    private baseUrl: string,
    private headers: Record<string, string> = {}
  ) {}

  async get<T = unknown>(
    key: string,
    options?: ReadOptions
  ): Promise<T | null> {
    try {
      const tableName = options?.tableName;
      const url = tableName
        ? `${this.baseUrl}/${tableName}/${key}`
        : `${this.baseUrl}/${key}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: this.headers,
      });

      if (response.status === 404) return null;
      if (!response.ok)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);

      return (await response.json()) as T;
    } catch (error) {
      if (error instanceof Error && error.message.includes('404')) {
        return null;
      }
      throw error;
    }
  }

  async put<T = unknown>(
    key: string,
    value: T,
    options?: WriteOptions & ExpirationOptions & MetadataOptions
  ): Promise<void> {
    const tableName = options?.tableName;
    const url = tableName
      ? `${this.baseUrl}/${tableName}/${key}`
      : `${this.baseUrl}/${key}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...this.headers,
      },
      body: JSON.stringify(value),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
  }

  async delete(key: string, options?: WriteOptions): Promise<boolean> {
    const tableName = options?.tableName;
    const url = tableName
      ? `${this.baseUrl}/${tableName}/${key}`
      : `${this.baseUrl}/${key}`;

    const response = await fetch(url, {
      method: 'DELETE',
      headers: this.headers,
    });

    if (response.status === 404) return false;
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return true;
  }

  async getAll<T = unknown>(
    options?: QueryOptions
  ): Promise<Array<{ key: string; value: T }>> {
    const tableName = options?.tableName;
    const url = tableName ? `${this.baseUrl}/${tableName}` : this.baseUrl;

    const queryParams = new URLSearchParams();
    if (options?.prefix) queryParams.set('prefix', options.prefix);
    if (options?.limit) queryParams.set('limit', options.limit.toString());
    if (options?.cursor) queryParams.set('cursor', options.cursor);

    const fullUrl = queryParams.toString() ? `${url}?${queryParams}` : url;

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: this.headers,
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return (await response.json()) as Array<{ key: string; value: T }>;
  }

  getCapabilities(): StoreCapabilities {
    return {
      storeType: 'http',
      features: {
        transactions: false,
        advancedQueries: false,
        aggregation: false,
        conditionalWrites: false,
        ttl: false,
        streaming: false,
      },
      limits: {},
      queryOperators: [],
      indexTypes: [],
    };
  }

  getNativeClient<C = typeof fetch>(): C {
    return fetch as unknown as C;
  }
}

// #endregion

// #region Convenience Functions & Examples

/**
 * Base class for projections that provides common functionality
 * like event filtering and revision tracking
 */
export abstract class BaseProjection<TEvent extends Event>
  implements Projection<TEvent>
{
  abstract readonly supportedEventTypes: string[];

  constructor(protected store: IReadModelStore) {}

  async processEvents(events: ReadEvent<TEvent>[]): Promise<void> {
    // Filter for events this projection supports
    const supportedEvents = events.filter((event) =>
      this.supportedEventTypes.includes(event.type)
    );

    if (supportedEvents.length === 0) return;

    // Process events sequentially to maintain order
    for (const event of supportedEvents) {
      await this.processEvent(event);
    }
  }

  /**
   * Process a single event - to be implemented by subclasses
   */
  protected abstract processEvent(event: ReadEvent<TEvent>): Promise<void>;

  /**
   * Helper method to check if an event should be processed based on revision
   */
  protected async shouldProcessEvent(
    event: ReadEvent<TEvent>,
    currentRevision?: number
  ): Promise<boolean> {
    // Skip processing if the entity already has a newer or equal revision
    if (
      currentRevision !== undefined &&
      currentRevision >= event.streamPosition
    ) {
      console.warn(
        `Skipping already processed event: ${event.type} for stream ${event.streamId}, current revision: ${currentRevision}, incoming position: ${event.streamPosition}`
      );
      return false;
    }

    return true;
  }

  /**
   * Helper method to validate revision for updates
   */
  protected validateRevision(
    event: ReadEvent<TEvent>,
    currentRevision: number
  ): boolean {
    const expectedRevision = event.streamPosition - 1;
    if (currentRevision !== expectedRevision) {
      console.error(
        `Invalid stream position for stream ${event.streamId}, expected revision: ${expectedRevision}, current revision: ${currentRevision}`
      );
      return false;
    }
    return true;
  }
}

/**
 * Create a projection handler with webhook support
 * This is platform-agnostic and works with any Request/Response implementation
 * @template TEvent - The event type(s) the projection can handle
 * @param projection - The projection instance to handle events
 * @returns A webhook handler function that processes HTTP requests
 * @public
 * @example
 * ```typescript
 * const projection = new UserProjection(store);
 * const webhookHandler = createWebhookProjectionHandler(projection);
 *
 * // Use in Cloudflare Worker, Vercel Edge Function, etc.
 * export default { fetch: webhookHandler };
 * ```
 */
export function createWebhookProjectionHandler<TEvent extends Event>(
  projection: Projection<TEvent>
) {
  return async (request: Request): Promise<Response> => {
    try {
      // Parse the incoming events
      const events = (await request.json()) as ReadEvent<TEvent>[];

      // Process the events
      await projection.processEvents(events);

      // Return success response
      return new Response(
        JSON.stringify({
          success: true,
          processedEvents: events.length,
          timestamp: new Date().toISOString(),
        }),
        {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        }
      );
    } catch (error) {
      console.error('Projection webhook error:', error);

      return new Response(
        JSON.stringify({
          error: error instanceof Error ? error.message : 'Unknown error',
          timestamp: new Date().toISOString(),
        }),
        {
          status: 500,
          headers: { 'Content-Type': 'application/json' },
        }
      );
    }
  };
}

// #endregion
